import { tierFor } from '../../shared/score';
import type { RuleDefinition, ScanReport, ScoreSignal } from '../../shared/types';

export type ExportNote = ScoreSignal & { n: number; rule: RuleDefinition };

const viewName = (viewport: string) => viewport === 'desktop' ? 'Desktop' : 'Phone';
const oneLine = (value: string, max = 160) => { const flat = value.replace(/\s+/g, ' ').trim(); return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat; };
const host = (url: string) => { try { const u = new URL(url); return (u.host + u.pathname).replace(/^www\./, '').replace(/\/$/, ''); } catch { return url; } };

// One readable document holding every note and every kept example from a scan, written for a person to review and for
// a coding agent to act on. The scan keeps at most six examples per rule and view, so the counts can exceed the lists.
export function buildReportMarkdown(report: ScanReport, notes: ExportNote[]): string {
  const score = report.score;
  const evidence = new Map(report.evidence.map(e => [e.id, e]));
  const page = report.pages[0];
  const when = (report.finishedAt ?? report.createdAt).slice(0, 10);
  const section = (title: string, intro: string, list: ExportNote[]) => {
    if (!list.length) return '';
    const items = list.map(note => {
      const found = report.findings.filter(f => f.ruleId === note.ruleId);
      const seen = `${note.count === 1 ? 'Spotted once' : `Spotted ${note.count} times`}${note.light ? ', counted lightly' : ''}`;
      const where = found.length ? found.map(f => {
        const e = f.evidenceIds.map(id => evidence.get(id)).find(Boolean);
        const quote = e?.text && e.text !== f.observed ? ` Text: “${oneLine(e.text)}”.` : '';
        return `- ${viewName(f.viewport)} view${e ? `, element \`${e.selector}\`` : ''}: ${oneLine(f.observed, 240)}${quote}`;
      }).join('\n') : '- This one was measured without a single element to point at, such as by pressing Tab through the page.';
      return [
        `### ${note.n}. ${note.rule.name}`,
        '',
        `${seen}, worth ${note.points} ${note.points === 1 ? 'point' : 'points'}.${note.rule.supporting ? ' This is a supporting signal, counted only because other template habits were found.' : ''}`,
        '',
        note.rule.plain,
        '',
        `Try this: ${note.rule.fix}`,
        '',
        `Where it was found (${found.length} ${found.length === 1 ? 'example' : 'examples'} kept):`,
        '',
        where,
      ].join('\n');
    });
    return `## ${title}\n\n${intro}\n\n${items.join('\n\n')}\n`;
  };
  const slop = notes.filter(n => n.rule.category === 'slop');
  const quality = notes.filter(n => n.rule.category === 'quality');
  return [
    `# SlopLens findings for ${host(page?.url ?? report.url)}`,
    '',
    `Scanned on ${when} with rule version ${report.ruleVersion}. ${score ? `The slop score is ${score.value} out of 100, in the tier called ${tierFor(score.value).name}.` : 'This scan did not produce a score.'} The address was ${page?.url ?? report.url}.`,
    '',
    '## How to use this list',
    '',
    'Every item below is a measured pattern on one rendered page, found by a checker rather than judged by a person. Treat each one as a candidate for review instead of a verdict, and keep any pattern that truly fits the product. The score cannot tell who or what built the website. When you fix a pattern, change the page so the underlying choice is different, instead of hiding what the checker looks at.',
    '',
    section('Template habits', 'These carry most of the score.', slop),
    section('Also worth fixing', 'These are usability problems, and they add only a few points.', quality),
    !notes.length ? 'No notes were recorded for this scan.\n' : '',
    `The scan keeps at most six examples for each rule and view, so a note can report more sightings than the examples listed here.`,
    '',
  ].filter((part, i, all) => part !== '' || all[i - 1] !== '').join('\n');
}

export const reportFileName = (report: ScanReport) => `sloplens-${host(report.pages[0]?.url ?? report.url).replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'report'}-${(report.finishedAt ?? report.createdAt).slice(0, 10)}.md`;

export function downloadText(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/markdown;charset=utf-8' }));
  const link = document.createElement('a'); link.href = url; link.download = name;
  document.body.append(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
